import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { getCart, calculateTotal } from '@/utils/cart';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Send, Copy, Share2, ArrowRight, Check } from 'lucide-react';
import { trackSharedCartCreated } from '@/hooks/usePostHogTracking';

interface CartShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CartShareModal: React.FC<CartShareModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<'name' | 'share'>('name');
  const [kidName, setKidName] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleContinue = () => {
    if (kidName.trim().length > 0) {
      setStep('share');
    }
  };

  const createSharedCart = async (email?: string) => {
    setIsLoading(true);
    try {
      const cart = getCart();
      const { data, error } = await supabase.functions.invoke('create-shared-cart', {
        body: {
          cartItems: cart.items,
          totalAmount: cart.total,
          kidName: kidName.trim(),
          guardianEmail: email?.trim() || undefined,
          language: i18n.language,
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      setShareUrl(data.shareUrl);
      return data.shareUrl;
    } catch (err: any) {
      console.error('Error creating shared cart:', err);
      toast.error(t('cartShare.error', 'Something went wrong. Please try again.'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!guardianEmail.trim()) return;
    const url = await createSharedCart(guardianEmail);
    if (url) {
      const cart = getCart();
      trackSharedCartCreated({
        share_method: 'email',
        kid_name: kidName.trim(),
        item_count: cart.items.length,
        total_amount: cart.total,
        share_token: shareUrl.split('/').pop() || '',
      });
      setEmailSent(true);
      toast.success(t('cartShare.emailSent'));
    }
  };

  const handleCopyLink = async () => {
    let url = shareUrl;
    if (!url) {
      url = await createSharedCart();
      if (!url) return;
    }

    const cart = getCart();
    const photoCount = cart.items.length;
    const lang = i18n.language;
    
    const message = lang === 'de'
      ? `Hallo! Ich habe ${photoCount} Foto${photoCount > 1 ? 's' : ''} auf Equiphoto ausgewählt. Kannst du sie dir ansehen und für mich kaufen? ${url}`
      : `Ciao! Ho selezionato ${photoCount} foto su Equiphoto. Puoi vederle e acquistarle per me? ${url}`;

    // Try native share on mobile, fallback to clipboard
    if (navigator.share) {
      try {
        await navigator.share({ text: message });
        toast.success(t('cartShare.linkShared'));
        return;
      } catch {
        // User cancelled or share failed, fall through to clipboard
      }
    }

    await navigator.clipboard.writeText(message);
    trackSharedCartCreated({
      share_method: 'link',
      kid_name: kidName.trim(),
      item_count: photoCount,
      total_amount: calculateTotal(cart.items),
      share_token: url.split('/').pop() || '',
    });
    setLinkCopied(true);
    toast.success(t('cartShare.linkCopied'));
    setTimeout(() => setLinkCopied(false), 3000);
  };

  const handleClose = () => {
    setStep('name');
    setKidName('');
    setGuardianEmail('');
    setShareUrl('');
    setLinkCopied(false);
    setEmailSent(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-on-surface">
            {step === 'name' 
              ? t('cartShare.enterNameTitle', 'Come ti chiami?')
              : t('cartShare.shareTitle', 'Condividi con un genitore')
            }
          </DialogTitle>
        </DialogHeader>

        {step === 'name' ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {t('cartShare.enterNameDescription', 'Inserisci il tuo nome così chi riceve il link saprà chi ha scelto le foto.')}
            </p>
            <Input
              value={kidName}
              onChange={(e) => setKidName(e.target.value)}
              placeholder={t('cartShare.namePlaceholder', 'Il tuo nome')}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
            />
            <Button 
              onClick={handleContinue} 
              disabled={kidName.trim().length === 0}
              className="w-full"
            >
              {t('cartShare.continue', 'Continua')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            <div className="rounded-lg bg-surface-variant p-4 text-center">
              <p className="text-base font-medium text-on-surface">
                {i18n.language === 'de'
                  ? `Tolle Auswahl, ${kidName}! 🎉`
                  : `Ottima scelta, ${kidName}! 🎉`
                }
              </p>
            </div>

            <p className="text-sm text-muted-foreground">
              {t('cartShare.shareDescription', 'Invia il link a un genitore per fargli vedere e acquistare le foto.')}
            </p>

            {/* Option A: Email */}
            {!emailSent ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-on-surface">
                  {t('cartShare.emailLabel', 'Email del genitore')}
                </label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={guardianEmail}
                    onChange={(e) => setGuardianEmail(e.target.value)}
                    placeholder={t('cartShare.emailPlaceholder', 'email@esempio.com')}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendEmail()}
                  />
                  <Button
                    onClick={handleSendEmail}
                    disabled={isLoading || !guardianEmail.trim()}
                    size="icon"
                    className="shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg bg-primary/10 p-3 text-primary">
                <Check className="h-5 w-5" />
                <span className="text-sm font-medium">
                  {t('cartShare.emailSentConfirm', 'Email inviata con successo!')}
                </span>
              </div>
            )}

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {t('cartShare.or', 'oppure')}
                </span>
              </div>
            </div>

            {/* Option B: Copy link */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleCopyLink}
              disabled={isLoading}
            >
              {linkCopied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  {t('cartShare.linkCopiedButton', 'Link copiato!')}
                </>
              ) : (
                <>
                  {navigator.share ? (
                    <Share2 className="mr-2 h-4 w-4" />
                  ) : (
                    <Copy className="mr-2 h-4 w-4" />
                  )}
                  {navigator.share
                    ? t('cartShare.shareLink', 'Condividi link')
                    : t('cartShare.copyLink', 'Copia link')
                  }
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
